import { Divider, Stack, Typography } from "@mui/material";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { TransactionIdEntryFormData } from "Components/Shared/Payments/useTransactionIdEntryForm";
import { Resources, useResource } from "Translations/Resources";
import type { FormEventHandler, ReactNode } from "react";
import type { Control, FieldErrors } from "react-hook-form";

const RESOURCES = Resources.Customer.Pay.ManualEntry;

type Props = {
  control: Control<TransactionIdEntryFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  // The heading varies with where the form sits — next to an NFC prompt it reads
  // as the fallback, on its own it is the primary instruction.
  title: string;
  // Optional line above the form; the caller owns its wording and tone.
  message?: ReactNode;
  // Overrides the field error, for a caller validating beyond the ID's shape.
  errors?: FieldErrors<TransactionIdEntryFormData>;
  inputEndAdornment?: ReactNode;
  // Rendered between the input and the submit button.
  belowInput?: ReactNode;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
};

export const TransactionIdEntryForm = ({
  control,
  onSubmit,
  title,
  message,
  errors,
  inputEndAdornment,
  belowInput,
  isSubmitDisabled,
  isSubmitLoading,
}: Props) => {
  const { t } = useResource();

  return (
    <Stack gap={3}>
      {!!message && (
        <>
          {message}
          <Divider />
        </>
      )}
      <Stack gap={2}>
        <Stack gap={0.5}>
          <Typography fontSize={16} fontWeight={600} color="text.primary">
            {title}
          </Typography>
          <Typography fontSize="0.875rem" color="text.secondary">
            {t(RESOURCES.ManualSubtitle)}
          </Typography>
        </Stack>
        <Stack component="form" gap={2} onSubmit={onSubmit}>
          <FormInput
            name="transactionPublicID"
            control={control}
            errors={errors}
            fullWidth
            disablePadding
            placeholder={t(RESOURCES.InputPlaceholder)}
            slotProps={{
              input: { endAdornment: inputEndAdornment },
              htmlInput: { "aria-label": t(RESOURCES.InputLabel) },
            }}
            valueTransformer={(value) => value.toUpperCase()}
          />
          {belowInput}
          <PrimaryButton
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            isLoading={isSubmitLoading}
            disabled={isSubmitDisabled}
          >
            {t(RESOURCES.SubmitButton)}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
